<template>
    <validation-observer ref="form" v-slot="{ handleSubmit, meta }">
        <modal
            :id="modalId"
            title="Settings"
            ok-title="Save"
            size="lg"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="handleSubmit(_,save)"
            @hide="hideModal"
            @shown="setData"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="handleSubmit(_, save)" v-if="values">
                <b-row class="settings-tab-wrapper mb-3">
                    <b-col cols="6" class="pr-1">
                        <b-button
                            class="w-100"
                            :active="getIsActive(communicationTab)"
                            exact-active-class="active-nav-button"
                            variant="default"
                            @click="currentTab = communicationTab"
                            href="#"
                            >Communication Settings</b-button
                        >
                    </b-col>
                    <b-col cols="6" class="pl-1">
                        <b-button
                            class="w-100"
                            :active="getIsActive(statementTab)"
                            exact-active-class="active-nav-button"
                            variant="default"
                            @click="setStatementTab"
                            href="#"
                            >Statement Settings</b-button
                        >
                    </b-col>
                </b-row>
                <span v-if="currentTab == communicationTab">
                    <label class="mb-2">Email subject:</label>
                    <lx-editor
                        v-model="values.owner_statement_email_subject"
                        :tagsList="tagsList"
                    ></lx-editor>
                    <label class="mt-3 mb-2">Email text (no HTML):</label>
                    <lx-editor
                        v-model="values.owner_statement_email_text"
                        :tagsList="tagsList"
                        :isTextArea="true"
                    ></lx-editor>
                    <form-textarea
                        class="mt-3"
                        :rows="3"
                        v-model="values.owner_statement_business_text"
                        label="Additional text in statement header (no HTML):"
                    ></form-textarea>
                </span>
                <span v-if="currentTab == statementTab">
                    <b-row>
                        <b-col>
                            <validation-provider
                                name="scale"
                                rules="required"
                                v-slot="{ errors, handleChange }"
                            >
                                <form-select
                                    label="Font Size:"
                                    v-model="values.owner_statement_scale"
                                    :options="scaleOptions"
                                    :error="errors[0]"
                                    @input="handleChange"
                                ></form-select>
                            </validation-provider>
                        </b-col>
                    </b-row>
                    <b-row
                        v-for="(
                            value, index
                        ) in values.owner_statement_column_settings"
                        :key="value.name + index"
                        class="mb-2"
                    >
                        <b-col>
                            <form-switch v-model="value.value" />
                            <span class="ml-2">{{ value.name }}</span>
                        </b-col>
                    </b-row>
                </span>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import API from '../../../api';
import { REPORT_OWNER_STATEMENT_SETTINGS_ENDPOINT } from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import Modal from '../../generic/modal/modal.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormSwitch from '../../generic/form-switch/form-switch.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import LxEditor from './lx-editor.vue';

export default {
    name: 'OwnerStatementSettingsModalForm',
    components: {
        Modal,
        BusyModal,
        FormSelect,
        FormSwitch,
        FormTextarea,
        LxEditor,
    },
    props: {
        modalId: String,
        settings: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            loading: false,
            values: this.getInitialValues(),
            currentTab: 'communication-settings',
            communicationTab: 'communication-settings',
            statementTab: 'statement-settings',
            tagsList: [
                { name: 'FROM DATE', value: '[FROM_DATE]' },
                { name: 'TO DATE', value: '[TO_DATE]' },
                { name: 'FILE NAME', value: '[FILE_NAME]' },
            ],
        };
    },
    computed: {
        scaleOptions() {
            let scaleOptions = [];
            for (var s = 0.4; s < 1.6; s += 0.1) {
                scaleOptions.push({
                    value: s.toFixed(1) * 1,
                    text: Math.round(s * 100) + '%',
                });
            }
            return scaleOptions;
        },
        requestData() {
            let data = cloneDeep(this.values);
            data.owner_statement_column_settings =
                this.values.owner_statement_column_settings.map((i) =>
                    i.value ? 0 : -1
                );
            return data;
        },
    },
    methods: {
        getInitialValues() {
            return {
                owner_statement_scale:
                    this.settings?.owner_statement_scale || 1,
                owner_statement_column_settings:
                    this.settings?.owner_statement_column_settings || [],
                owner_statement_email_subject:
                    this.settings?.owner_statement_email_subject || '',
                owner_statement_email_text:
                    this.settings?.owner_statement_email_text || '',
                owner_statement_business_text:
                    this.settings?.owner_statement_business_text || '',
            };
        },
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        getIsActive(tab) {
            return this.currentTab === tab;
        },
        async save() {
            try {
                this.loading = true;
                await API.patch(
                    REPORT_OWNER_STATEMENT_SETTINGS_ENDPOINT(),
                    this.requestData
                );
                this.successToast({
                    text: 'Settings were successfully saved.',
                });
                this.$nextTick(() => {
                    this.hideModal();
                });
            } catch (error) {
                const { generic, fields } = formatResponseError(error);
                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.form.setErrors(fields);
                }
            } finally {
                this.loading = false;
            }
        },
        setData() {
            this.$refs.form.setValues({
                scale: this.values.owner_statement_scale
            })
        },
        setStatementTab() {
            this.currentTab = this.statementTab;
            this.$refs.form.setValues({
                scale: this.values.owner_statement_scale
            })
        },
    },
    watch: {
        settings() {
            this.values = this.getInitialValues();
        },
    },
};
</script>

<style lang="scss" scoped>
    :deep(.settings-tab-wrapper) {
        a.active {
            background-color: transparent !important;
            border-color: #ccc !important;
            box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.3) !important;
        }
    }
</style>